<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- ============================================================
                 HERO
                 ============================================================ -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 50px;">
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="celebration" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <span class="mkt-trust-pill mkt-trust-pill--light q-mb-md" style="display:inline-block;">
                        <q-icon name="celebration" class="mkt-trust-icon" /> For photographers, planners, caterers, DJs
                    </span>
                    <h1 class="text-weight-bold q-mt-md q-mb-md" style="font-size:clamp(2rem,4vw,3rem);color:#0F172A;line-height:1.15;">
                        Lock the date.<br>
                        <span class="text-gradient-indigo">Lock the deposit.</span>
                        <span class="visually-hidden"> - Wedding vendor contracts and deposit collection</span>
                    </h1>
                    <p class="q-mb-lg" style="font-size:1.1rem;max-width:620px;margin:0 auto;color:#475569;">
                        Couples sign your contract and pay the deposit in the same flow. The booking holds
                        when the deposit clears - not when you finally remember to follow up.
                    </p>
                    <div class="row justify-center q-gutter-md mkt-hero-cta-row">
                        <q-btn color="primary" size="lg" label="Start Free" href="/Account/Register?plan=collect"
                            no-caps unelevated icon-right="arrow_forward"
                            style="border-radius:10px;padding:10px 32px;font-weight:600;" />
                        <q-btn outline size="lg" no-caps label="See how it works" href="#workflow"
                            style="border-radius:10px;padding:10px 32px;color:#334155;border-color:#CBD5E1;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 WORKFLOW
                 ============================================================ -->
            <section id="workflow" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">How it works</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">From consultation to confirmed booking - in the same conversation</h2>
                    </div>
                    <div class="row q-col-gutter-xl items-stretch">
                        <div class="col-12 col-md-4" v-for="(step, i) in steps" :key="i">
                            <div class="mkt-step-tile">
                                <div class="mkt-step-ribbon">{{ String(i + 1).padStart(2, '0') }}</div>
                                <div class="row items-center q-gutter-sm q-mb-sm">
                                    <q-icon :name="step.icon" size="28px" color="primary" />
                                    <h3 class="text-weight-bold q-ma-none" style="font-size:1.2rem;color:#0a0f1a;line-height:1.2;">{{ step.title }}</h3>
                                </div>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ step.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 ROI CALLOUT
                 ============================================================ -->
            <section class="mkt-light reveal-fade-in" v-intersection="revealCfg" style="padding:50px 0;">
                <div class="mkt-container-narrow">
                    <div class="mkt-card q-pa-xl" style="border-left:4px solid var(--mkt-indigo);">
                        <div class="row items-center q-col-gutter-lg">
                            <div class="col-12 col-md-4 text-center">
                                <div class="text-weight-bold" style="font-size:3rem;letter-spacing:-0.02em;line-height:1;color:#0F172A;">
                                    <span style="background:linear-gradient(135deg,#047857,#0D9488);-webkit-background-clip:text;background-clip:text;color:transparent;">$1,500</span>
                                </div>
                                <div class="text-grey-7 q-mt-xs" style="font-size:0.85rem;">deposit collected at the consultation</div>
                            </div>
                            <div class="col-12 col-md-8">
                                <h3 class="text-weight-bold q-mb-sm" style="font-size:1.15rem;color:#0a0f1a;">Real flow</h3>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.7;">
                                    Saturday consultation at the coffee shop. You pull up the contract on your phone,
                                    add the couple's names, hit send. They sign on their phone, drop in the deposit, and
                                    the date is held before you've finished your latte.
                                    The remaining balance can auth-hold now and capture two weeks before the wedding.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 VERTICAL FEATURES
                 ============================================================ -->
            <section id="features" class="mkt-white reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Built for event work</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">What you get</h2>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-6 col-lg-4" v-for="f in features" :key="f.title">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon :name="f.icon" size="32px" :style="`color:${f.color};`" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.05rem;color:#0a0f1a;line-height:1.25;">{{ f.title }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.9rem;line-height:1.6;">{{ f.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 PRICING
                 ============================================================ -->
            <section id="pricing" class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-lg">
                        <div class="mkt-section-eyebrow">Pricing</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Sign & Collect - built for booking flows</h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:560px;margin:0 auto;">
                            $39/mo for 50 signings, plus a 2.5% fee on collected deposits and final payments.
                            Self-serve onboarding.
                        </p>
                    </div>
                    <div style="max-width:400px;margin:0 auto;">
                        <plan-card tier-code="collect"
                                   featured featured-label="For event vendors"
                                   icon="payments" accented
                                   cta-label="Start with Sign & Collect"
                                   includes-label="What you get:" />
                    </div>
                    <div class="text-center q-mt-lg">
                        <span style="font-size:0.9rem;color:#475569;">Booking 100+ events a year?</span>
                        <q-btn flat no-caps color="primary" label="See Pro plan"
                               icon-right="arrow_forward" href="/Home/SmallBusiness#plan-pro"
                               class="q-ml-sm"
                               style="font-size:0.9rem;font-weight:500;border-radius:8px;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FAQ
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">FAQ</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Common questions from event vendors</h2>
                    </div>
                    <q-list>
                        <q-expansion-item v-for="faq in faqs" :key="faq.q" :label="faq.q"
                            header-class="text-weight-bold" style="border-bottom:1px solid rgba(0,0,0,0.06);">
                            <q-card>
                                <q-card-section class="text-grey-7" style="font-size:0.95rem;line-height:1.7;">
                                    {{ faq.a }}
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-list>
                </div>
            </section>

            <!-- ============================================================
                 FINAL CTA
                 ============================================================ -->
            <section class="mkt-cta-gradient reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Lock your next booking today.</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">Self-serve. Secure card processing. Cancel anytime.</p>
                    <q-btn color="white" text-color="primary" size="lg" label="Start Free" href="/Account/Register?plan=collect"
                        no-caps unelevated icon-right="arrow_forward"
                        style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                </div>
            </section>

            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<script>
import PlanCard from '@components/marketing/PlanCard.vue';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'ForWeddingVendorsPage',
    mixins: [revealMixin],
    components: { PlanCard, MarketingNav, MarketingFooter },
    data() {
        return {
            mobileNav: false,

            steps: [
                { icon: 'description', title: 'Send the contract',
                    detail: 'Pull up your saved template (Photographer/Couple, Caterer/Client, etc.), drop in event details, hit send.' },
                { icon: 'phone_iphone', title: 'They sign + pay on their phone',
                    detail: 'Mobile-friendly signing in the browser. The deposit drops in next, on the same screen, before they put the phone down.' },
                { icon: 'event_available', title: 'Booking confirmed',
                    detail: 'Date held, deposit settled to your account. The remaining balance can auth-hold now or capture closer to the event.' }
            ],

            features: [
                { icon: 'payments',          color: '#047857', title: 'Deposit + final-payment flow',
                    detail: 'Capture the deposit at booking. Auth-hold the balance and capture two weeks before the event. One contract, two payment events.' },
                { icon: 'account_balance',   color: '#3949ab', title: 'Instant Bank Payments at the venue',
                    detail: 'Couple paying their deposit by bank transfer? Funds settle the moment they hit Submit — no waiting days for ACH to clear before the date is held.' },
                { icon: 'event',             color: '#B45309', title: 'Event templates',
                    detail: 'Save your wedding photography contract once. Use roles like "Photographer" and "Couple" so each new booking is one fill-in.' },
                { icon: 'phone_iphone',      color: '#2563EB', title: 'Phone-first signing',
                    detail: 'Couples sign on the device in their hand. No app install, no account required. Tap, sign, pay.' },
                { icon: 'palette',           color: '#7C3AED', title: 'Branded for your studio',
                    detail: 'Your logo, brand colors, and reply-to address on the signing ceremony and the email the couple receives.' },
                { icon: 'notifications_active', color: '#0EA5E9', title: 'Automatic reminders',
                    detail: 'Schedule a reminder for the final payment as the event date approaches. No more "oh wait, did we capture that?"' },
                { icon: 'undo',              color: '#0D9488', title: 'Refunds when life happens',
                    detail: 'Issue a full or partial refund from the dashboard if a couple cancels, in line with your contract terms.' }
            ],

            faqs: [
                { q: 'Can I split the deposit and final payment into two charges?',
                    a: 'Yes. Charge the deposit at signing, then either auth-hold the final payment now (capture closer to the event) or schedule it as a separate reminder. The contract captures both events as one record.' },
                { q: 'What if a couple cancels?',
                    a: 'Issue a full or partial refund from the dashboard with one click - the audit trail records the refund alongside the original payment. Your contract\'s cancellation terms govern; we\'re just the rails.' },
                { q: 'Can I customize the contract template for my studio?',
                    a: 'Yes - upload your own contract PDF or Word doc, pre-place signature and initial fields, save as a template. Roles like "Photographer" and "Couple" let you reuse it for every booking.' },
                { q: 'Does this work for caterers / DJs / planners / florists?',
                    a: 'Yes - anyone collecting a deposit-and-final-payment from a couple. The same template + flow works whether you\'re booking the venue or the cake.' },
                { q: 'How does the couple know what they\'re signing?',
                    a: 'They see the full contract before signing - scrollable on phone, tablet, or desktop. They can\'t pay until they\'ve signed all required fields. Every action is logged with timestamp and IP for the audit trail.' },
                { q: 'Is the signed contract legally binding?',
                    a: 'Yes - signatures are ESIGN Act and UETA compliant in all 50 states, with hash-chained audit trail and certificate of completion attached. Standard for event contracts.' }
            ]
        };
    }
};
</script>

<style scoped>
.mkt-section-eyebrow {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #4F46E5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
}
</style>
